/**
 * W-IA-2c (IA_CANON_v2 раздел 2.6) — рельса метрик на Сегодня.
 *
 * Заменяет три отдельные mini-карты одним горизонтальным рядом компактных
 * glance-чипов. Tap по чипу с drill-key → MetricDetailDrawer; без —
 * своя страница. v4-правило: число ink-1, семантика — строкой под числом.
 *
 * Настраиваемость (ось «любитель↔элита», IA_CANON раздел 0): атлет закрепляет
 * свои метрики; выбор в localStorage (паттерн ws-пресетов) per-device.
 * Дефолт — первые 6 доступных.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { GearSix } from "@phosphor-icons/react";

import { BottomSheet } from "../BottomSheet";

const PINS_KEY = "storica_glance_pins_v1";
const DEFAULT_PIN_COUNT = 6;

export interface GlanceItem {
  key: string;
  label: string;
  value: string;
  /** Строка под числом — Δ/статус. Красится семантикой, число — нет. */
  sub?: string;
  subTone?: "good" | "warn" | "bad" | "muted";
  onClick?: () => void;
}

const SUB_TONE_CLASS: Record<NonNullable<GlanceItem["subTone"]>, string> = {
  good: "text-ok",
  warn: "text-warn",
  bad: "text-bad",
  muted: "text-fg-muted",
};

function loadPins(): string[] | null {
  try {
    const raw = localStorage.getItem(PINS_KEY);
    if (!raw) return null;
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((x) => typeof x === "string") : null;
  } catch {
    // silent: приватный режим/квота — живём с дефолтом
    return null;
  }
}

export function GlanceRail({ items }: { items: GlanceItem[] }) {
  const { t } = useTranslation("dashboard");
  const [pins, setPins] = useState<string[] | null>(() => loadPins());
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    if (pins === null) return;
    try {
      localStorage.setItem(PINS_KEY, JSON.stringify(pins));
    } catch {
      // silent: storage недоступен — настройка session-only
    }
  }, [pins]);

  const visible = useMemo(() => {
    if (pins === null) return items.slice(0, DEFAULT_PIN_COUNT);
    const set = new Set(pins);
    return items.filter((it) => set.has(it.key));
  }, [items, pins]);

  if (items.length === 0) return null;

  const effectivePins = pins ?? items.slice(0, DEFAULT_PIN_COUNT).map((i) => i.key);

  return (
    <div data-testid="today-glance-rail" className="flex gap-1.5 overflow-x-auto pb-1" role="list">
      {visible.map((it) => {
        const body = (
          <>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-fg-muted whitespace-nowrap">
              {it.label}
            </div>
            <div className="text-sm font-semibold text-fg-primary num-display whitespace-nowrap">
              {it.value}
            </div>
            {it.sub && (
              <div className={`text-[10px] font-mono whitespace-nowrap ${SUB_TONE_CLASS[it.subTone ?? "muted"]}`}>
                {it.sub}
              </div>
            )}
          </>
        );
        const cls = "shrink-0 min-w-[86px] px-2.5 py-1.5 rounded-tile bg-bg-raised text-left";
        return it.onClick ? (
          <button key={it.key} type="button" role="listitem" data-testid={`glance-${it.key}`} onClick={it.onClick} className={`${cls} hover:brightness-110`}>
            {body}
          </button>
        ) : (
          <div key={it.key} role="listitem" data-testid={`glance-${it.key}`} className={cls}>
            {body}
          </div>
        );
      })}

      <button
        type="button"
        data-testid="glance-edit"
        onClick={() => setEditOpen(true)}
        aria-label={t("glance.edit", { defaultValue: "Настроить метрики" })}
        className="shrink-0 px-2 rounded-tile bg-bg-raised/60 text-fg-muted hover:text-fg-primary flex items-center"
      >
        <GearSix size={14} aria-hidden />
      </button>

      <BottomSheet
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title={t("glance.edit_title", { defaultValue: "Рельса метрик" })}
        subtitle={t("glance.edit_hint", { defaultValue: "Закрепи свои — любитель держит сон и зоны, профи — EF и W′" })}
        testId="glance-edit-sheet"
      >
        <div className="space-y-1 px-1 pb-2">
          {items.map((it) => {
            const on = effectivePins.includes(it.key);
            return (
              <button
                key={it.key}
                type="button"
                data-testid={`glance-pin-${it.key}`}
                onClick={() => {
                  const next = on
                    ? effectivePins.filter((k) => k !== it.key)
                    : [...effectivePins, it.key];
                  setPins(next);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-tile hover:bg-bg-raised active:bg-bg-raised transition-colors"
              >
                <span className="text-sm text-fg-primary">{it.label}</span>
                <span
                  className={`w-8 h-[18px] rounded-full relative transition-colors ${on ? "bg-accent" : "bg-bg-raised ring-1 ring-bg-divider"}`}
                  aria-hidden
                >
                  <span
                    className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white transition-all ${on ? "right-0.5" : "left-0.5"}`}
                  />
                </span>
              </button>
            );
          })}
        </div>
      </BottomSheet>
    </div>
  );
}
